import * as React from 'react'

import { Box, SearchField, SortDirection, Table } from '@factorialco/deprecated-design-system'
import orderBy from 'lodash/orderBy'

import { observer } from 'mobx-react'

import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import FiltersForm from 'modules/core/lib/forms/Filters'
import i18n from 'modules/core/lib/i18n'

import {
  filterByEmployee,
  filterBySearch,
  filterByStatus,
} from 'modules/goals/components/Table/filtering'
import goalsConfig from 'modules/goals/store/collections/goalsconfig'
import type GoalConfig from 'modules/goals/store/models/GoalConfig'

import Body from './_Body'
import Filters, { GoalsConfigFilters } from './_Filters'
import Header from './_Header'

type Props = {
  canSeeClockedIn: boolean
}

const GoalConfigTable = ({ canSeeClockedIn }: Props) => {
  const { search, onSearch } = useDebouncedSearch()
  const [direction, setDirection] = React.useState<SortDirection>('asc')
  const [attribute, setAttribute] = React.useState<string>('name')
  const [status, setStatus] = React.useState<string>('')
  const [filterBy, setFilterBy] = React.useState<string>('')

  const filtersForm = new GoalsConfigFilters({
    status: status,
  })

  const employeesFiltersForm = new FiltersForm({
    filterBy: filterBy,
  })

  const [filteredGoalsConfig, setFilteredGoalsConfig] = React.useState<{
    loading: boolean
    value: GoalConfig[]
  }>({ loading: false, value: [] })

  React.useEffect(() => {
    const filterFn = async () => {
      const filteredBySearch = filterBySearch(search)
      const filteredByStatus = filterByStatus(filteredBySearch, status)
      const filteredByEmployee = await filterByEmployee(
        filteredByStatus,
        employeesFiltersForm,
        filterBy
      )

      const result = orderBy(filteredByEmployee, (goalConfig) => goalConfig.attributes[attribute], [
        direction,
      ])

      setFilteredGoalsConfig({ loading: false, value: result })
    }
    filterFn()
  }, [search, direction, attribute, goalsConfig.models.length, status, filterBy])

  const onSort = (sortAttribute: string, sortDirection: SortDirection) => {
    setDirection(sortDirection)
    setAttribute(sortAttribute)
  }

  const onFilterStatus = (statusFilter: string) => setStatus(statusFilter)
  const onFilterEmployee = (employeeFilter: string) => setFilterBy(employeeFilter)

  const TableOptions = () => (
    <Box flexDirection="row" gap="s8" flexGrow alignItems="center">
      <Filters
        filtersForm={filtersForm}
        employeesFiltersForm={employeesFiltersForm}
        onFilterStatus={onFilterStatus}
        onFilterEmployee={onFilterEmployee}
        canSeeClockedIn={canSeeClockedIn}
      />
      <Box width="full">
        <SearchField
          variant="big"
          search={search}
          onSearch={onSearch}
          placeholder={i18n.t('common.search')}
        />
      </Box>
    </Box>
  )

  return (
    <Table options={TableOptions} fullWidth>
      <Header onSort={onSort} attribute={attribute} direction={direction} />
      <Body filteredGoalsConfigCollection={filteredGoalsConfig.value} />
    </Table>
  )
}

export default observer(GoalConfigTable)
